/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import * as React from 'react';
import { logURlLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type ExternalLinkProps = ILinkProps & {
    /**
     * The page or component where the click
     * originated from.
     */
    activitySource?: string;

    /**
     * Whether to open the link in a new tab
     * or not.
     * @default true
     */
    inNewTab?: boolean;

    /**
     * Whether the link should be displayed with
     * an underline or not.
     */
    isUnderlined?: boolean;

    /**
     * Whether the link background is in dark mode or not.
     */
    isDarkMode?: boolean;

    innerRef?: React.RefObject<HTMLElement>;

    isLandingPageLearnMoreLink?: boolean;
};

const theme = getScopedTheme('ExternalLink');

/**
 * A Link that opens in a new tab by default
 * and logs telemetry on click.
 */
export const ExternalLink = React.forwardRef<HTMLElement, ExternalLinkProps>((props, ref) => {
    const {
        isLandingPageLearnMoreLink,
        activitySource,
        inNewTab = true,
        isUnderlined = true,
        href,
        onClick,
        isDarkMode = false,
        innerRef, // We will handle `innerRef` below.
        ...rest
    } = props;
    const loggerRef = React.useRef(getLogger(activitySource));

    const linkStyles = {
        root: mergeStyles({
            textDecoration: isUnderlined ? 'underline' : 'none',
            color: !props.disabled && `${isDarkMode ? theme.darkModeColor : theme.lightModeColor} !important`
        })
    };

    const onClickLink = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLElement>) => {
        loggerRef.current?.logEvent('ExternalLink:URL:Click', { href, isUserAction: true });
        if (isLandingPageLearnMoreLink) {
            logURlLinkClick('landing page', '', 'LearnMore');
        }

        if (onClick) {
            onClick(event);
        }
    };

    return (
        <Link
            href={href}
            rel="noopener noreferrer"
            target={inNewTab ? '_blank' : '_self'}
            {...rest}
            ref={ref || innerRef} // Use `ref` provided by `React.forwardRef`, fallback to `innerRef` for backward compatibility.
            onClick={onClickLink}
            styles={linkStyles}
        />
    );
});
